<template>
  <v-card class="lean-linked-documents mx-auto my-4" max-width="1000" :rounded="false" outlined>
    <v-toolbar color="blue-grey lighten-1" dark flat dense>
      <v-toolbar-title>{{ label }}</v-toolbar-title>
      <v-spacer />
      <lean-list-mode :datatype="config.source" />
      <lean-create-linked-document-dialog :datatype="this.config.source" @afterSave="addToList($event)" />
    </v-toolbar>
    <v-container fluid class="px-10 py-5">
      <lean-search-record :config="config" :disabled="disabled" @change="addRecordToList($event)" />
      <draggable
        v-if="listMode === 'list' && showDraggable"
        v-model="value"
        @update="syncResolved"
        style="min-height: 10px"
        handle=".handle"
        @change="refreshDraggable()"
      >
        <template v-for="(record, index) in value">
          <v-list-item
            v-if="resolvedItems[index] && !resolvedItems[index].error"
            :key="index"
            class="lean-linked-documents__list-item px-0"
          >
            <lean-list-item-content
              @click="smallDialog = true"
              :record="getRecord(resolvedItems[index], listConfig)"
              :listConfig="listConfig"
            >
              <template v-slot:actions>
                <div class="d-flex">
                  <v-btn
                    v-if="!!resolvedItems && resolvedItems[index]"
                    :href="`/data/${config.source}/${resolvedItems[index]._leancms.slug}`"
                    target="new"
                    icon
                  >
                    <v-icon color="grey lighten-1">mdi-pencil</v-icon>
                  </v-btn>
                  <lean-list-item-button
                    icon-name="mdi-link-variant-off"
                    :tooltip="$t('tooltip.unlink')"
                    @click="removeElement(index)"
                  />
                  <v-icon class="lean-linked-documents-list-item__drag pl-4 handle" color="grey lighten-1">
                    mdi-drag-vertical
                  </v-icon>
                </div>
              </template>
            </lean-list-item-content>
          </v-list-item>
          <v-list-item v-else :key="index" class="lean-linked-documents__list-item px-0">
            <div>
              {{ resolvedItems[index].path }}<br />
              <small>{{ resolvedItems[index].error }}</small>
            </div>
            <lean-list-item-button
              icon-name="mdi-link-variant-off"
              :tooltip="$t('tooltip.unlink')"
              @click="removeElement(index)"
            />
          </v-list-item>
        </template>
      </draggable>
      <v-container v-else-if="listMode === 'cards'" fluid>
        <draggable v-if="showDraggable" v-model="value" @update="syncResolved" handle=".handle" class="row dense" @change="refreshDraggable()">
          <v-col v-for="(record, index) in value" :key="index" :cols="3">
            <lean-list-item-card
              @click="smallDialog = true"
              :record="getRecord(resolvedItems[index], listConfig)"
              :listConfig="listConfig"
            >
              <template v-slot:actions>
                <div class="d-flex">
                  <v-btn
                    v-if="!!resolvedItems && resolvedItems[index]"
                    :href="`/data/${config.source}/${resolvedItems[index]._leancms.slug}`"
                    target="new"
                    icon
                  >
                    <v-icon color="grey lighten-1" small>mdi-pencil</v-icon>
                  </v-btn>
                  <lean-list-item-button
                    icon-name="mdi-link-variant-off"
                    :tooltip="$t('tooltip.unlink')"
                    @click="removeElement(index)"
                    :x-small="true"
                  />
                  <v-icon class="lean-linked-documents-list-item__drag handle" color="grey lighten-1">
                    mdi-drag-vertical
                  </v-icon>
                </div>
              </template>
            </lean-list-item-card>
          </v-col>
        </draggable>
      </v-container>
    </v-container>
  </v-card>
</template>

<script>
import fieldMixin from '@/mixins/LeanField';
import LeanListItemContent from '@/components/LeanListItemContent.vue';
import LeanListItemCard from '@/components/LeanListItemCard.vue';
import draggable from 'vuedraggable';
import { mapState } from 'vuex';
import LeanListItemButton from '@/components/LeanListItemButton';
import LeanListMode from '@/components/LeanListMode.vue';
import LeanCreateLinkedDocumentDialog from '../LeanCreateLinkedDocumentDialog.vue';
import LeanSearchRecord from '../LeanSearchRecord.vue';

export default {
  name: 'LeanLinkedDocuments',
  components: {
    LeanListItemButton,
    LeanListItemContent,
    LeanListItemCard,
    LeanListMode,
    draggable,
    LeanCreateLinkedDocumentDialog,
    LeanSearchRecord
  },
  mixins: [fieldMixin],
  data() {
    return {
      allowDuplicates: false,
      title: 'slug',
      items: [],
      resolvedItems: this.resolved,
      loading: false,
      error: false,
      errorMessages: null,
      search: null,
      bounceSearch: null,
      searchInterval: null,
      showDraggable: true
    };
  },
  computed: {
    ...mapState({
      language: 'selectedLanguage',
      datatypes: 'models'
    }),
    searchLabel() {
      return `Search for ${this.config.source}`;
    },
    listConfig() {
      return this.datatypes[this.config.source].list || null;
    },
    listMode() {
      return (
        this.$store.state.AdminUserStore.listMode.find((item) => item.datatype === this.config.source)?.mode ?? 'list'
      );
    },
    index() {
      return (this.value || []).map((record) => record.slug);
    }
  },
  watch: {
    search(val) {
      console.log('val', val);
      if (val) {
        clearTimeout(this.bounceSearch);
        this.bounceSearch = setTimeout(() => {
          this.querySelections(val);
        }, 500);
      }
    },
    items(values) {
      if (values.length) {
        this.setResultFieldLabel(values);
      }
    }
  },
  methods: {
    // helper method to get by dotnotation
    _get(object, path, defval = null) {
      if (typeof path === 'string') path = path.split('.');
      return path.reduce((xs, x) => (xs && xs[x] ? xs[x] : defval), object);
    },
    reorderArray(arr, oldIndex, newIndex) {
      if (newIndex >= arr.length) {
        let k = newIndex - arr.length + 1;
        while (k--) {
          arr.push(undefined);
        }
      }
      arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    },
    syncResolved(evt) {
      this.reorderArray(this.resolvedItems, evt.oldIndex, evt.newIndex);
    },
    toggleListMode() {
      if (this.listMode === 'list') {
        this.listMode = 'cards';
      } else {
        this.listMode = 'list';
      }
    },
    addRecordToList(dbRecord) {
      console.log('dbRecord', dbRecord);
      if (!this.allowDuplicates && this.index.indexOf(dbRecord) !== -1) {
        this.error = true;
        // TODO: i18n
        this.errorMessages = 'This record is already linked.';
        return;
      }
      if (!this.value) {
        this.value = [];
      }
      if (!this.resolvedItems) {
        this.resolvedItems = [];
      }
      this.resolvedItems.push(dbRecord.resolved_data);
      this.value.push(dbRecord.slug);
      this.isModified = true;
    },
    removeElement(index) {
      this.value.splice(index, 1);
      this.resolvedItems.splice(index, 1);
    },
    async querySelections(val) {
      this.loading = true;
      this.items = [
        ...((
          await this.$store.state.sdk.getDocuments({
            datatype: this.config.source,
            language: this.language,
            query: val
          })
        ).records || []),
        ...((
          await this.$store.state.sdk.getDocuments({
            datatype: this.config.source,
            language: this.language,
            filenames: [`data/${this.config.source}/${val}/${this.language}/index.json`]
          })
        ).records || [])
      ];
      this.loading = false;
    },
    addToList(slug) {
      this.loading = true;
      let dbRecord;
      let count = 0;
      new Promise((resolve) => {
        this.searchInterval = setInterval(async () => {
          await this.querySelections(slug);
          dbRecord = this.items.find((item) => item.slug === slug);
          count++;
          if (!!dbRecord || count > 30) clearInterval(this.searchInterval);
          if (dbRecord) resolve(dbRecord);
        }, 1000);
      }).then((record) => {
        this.addRecordToList(record);
      });
      this.loading = false;
    },
    getRecord(record, listConfig) {
      const newRecord = { ...record };
      const currentRecordConfig = this.datatypes[this.config.source];

      // check every field which is showing in the component.
      // when the field is a record/datatype field then get the value of the field from the subrecord
      for (const key of Object.keys(listConfig)) {
        const fieldnames = Array.isArray(listConfig[key]) ? listConfig[key] : [listConfig[key]];

        for (const fieldname of fieldnames) {
          const recordFieldConfig = currentRecordConfig.schema.find((fieldConfig) => fieldConfig.name === fieldname);
          if (
            !!newRecord[fieldname] &&
            !!recordFieldConfig &&
            ['document', 'document[]'].includes(recordFieldConfig.type)
          ) {
            const subRecordConfig = this.datatypes[recordFieldConfig.source];
            const neededField = subRecordConfig.list[key];
            if (neededField) {
              const records = Array.isArray(newRecord[fieldname]) ? newRecord[fieldname] : [newRecord[fieldname]];
              // fetch subrecord from bucket
              newRecord[fieldname] = records.map((record) => record[neededField]);
            }
          }
        }
      }
      return newRecord;
    },
    refreshDraggable(){
      this.showDraggable= false;
      this.$nextTick(()=>this.showDraggable = true);
    }
  }
};
</script>

<style scoped>
.lean-linked-documents .lean-linked-documents-list-item__drag {
  cursor: move;
}
</style>
